/**
 * Razorpay Service
 * Handles Razorpay script loading, order creation, payment verification,
 * and checkout modal initialization.
 */

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

/**
 * Load Razorpay checkout script dynamically
 * @returns {Promise<boolean>} True if loaded successfully
 */
export const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    // Already loaded
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

/**
 * Create a Razorpay order via backend
 * @param {object} params
 * @param {string} params.planType - "silver" | "gold" | "platinum"
 * @param {number} params.amount  - Amount in paise (₹129.99 → 12999)
 * @param {string} params.userId  - Firebase user ID
 * @returns {Promise<{orderId: string, amount: number, currency: string, keyId: string}>}
 */
export const createOrder = async ({ planType, amount, userId }) => {
  const response = await fetch(`${BACKEND_URL}/api/payment/create-order`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ planType, amount, userId })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Server error (${response.status})`);
  }

  const data = await response.json();
  if (!data.success) {
    throw new Error(data.error || 'Failed to create order');
  }
  return data;
};

/**
 * Verify payment on backend after successful Razorpay checkout
 * @param {object} params
 * @returns {Promise<{success: boolean, planType: string, planExpiryDate: string, transactionId: string}>}
 */
export const verifyPayment = async ({
  razorpay_order_id,
  razorpay_payment_id,
  razorpay_signature,
  userId,
  planType,
  promoCode
}) => {
  const response = await fetch(`${BACKEND_URL}/api/payment/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      razorpay_order_id,
      razorpay_payment_id,
      razorpay_signature,
      userId,
      planType,
      promoCode: promoCode || null
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Verification failed (${response.status})`);
  }

  const data = await response.json();
  if (!data.success) {
    throw new Error(data.error || 'Payment verification failed');
  }
  return data;
};

/**
 * Open Razorpay Checkout Modal
 * Returns a promise that resolves on successful payment or rejects on cancel/failure.
 * On rejection, the error object includes `orderId` so caller can do server-side status check.
 *
 * @param {object} options
 * @param {string} options.orderId  - Razorpay order ID from createOrder()
 * @param {number} options.amount   - Amount in paise
 * @param {string} options.currency - "INR"
 * @param {string} options.keyId    - Razorpay public key
 * @param {string} options.userEmail
 * @param {string} options.userName
 * @param {string} options.planName - e.g. "Gold"
 * @returns {Promise<{razorpay_payment_id: string, razorpay_order_id: string, razorpay_signature: string}>}
 */
export const openCheckout = ({ orderId, amount, currency, keyId, userEmail, userName, planName }) => {
  return new Promise((resolve, reject) => {
    const options = {
      key: keyId,
      amount: amount,
      currency: currency,
      name: 'ResumeGlobe',
      description: `${planName} Plan Purchase`,
      image: '/logo.png', // Logo in /public folder
      order_id: orderId,
      prefill: {
        name: userName || '',
        email: userEmail || ''
      },
      notes: {
        planName: planName
      },
      theme: {
        color: '#10b981' // ResumeGlobe green
      },
      method: {
        upi: true,
        netbanking: true,
        card: true,
        wallet: true,
        paylater: true
      },
      handler: (response) => {
        // Success: response has razorpay_payment_id, razorpay_order_id, razorpay_signature
        resolve(response);
      },
      modal: {
        ondismiss: () => {
          const err = new Error('Payment cancelled by user');
          err.orderId = orderId;
          reject(err);
        },
        escape: true,
        backdropclose: false
      }
    };

    const rzp = new window.Razorpay(options);

    rzp.on('payment.failed', (response) => {
      const err = new Error(response.error.description || 'Payment failed');
      err.orderId = orderId;
      err.razorpayErrorCode = response.error.code;
      reject(err);
    });

    rzp.open();
  });
};

/**
 * Check order payment status on the server (Razorpay API).
 * Used when the frontend checkout fires payment.failed or ondismiss,
 * to confirm whether money was actually captured on Razorpay's side.
 *
 * @param {object} params
 * @param {string} params.orderId - The Razorpay order ID
 * @param {string} params.userId  - Firebase user ID
 * @param {string} params.planType - "silver" | "gold" | "platinum"
 * @returns {Promise<{success: boolean, status: string, recovered?: boolean, planType?: string}>}
 */
export const checkOrderStatus = async ({ orderId, userId, planType }) => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/payment/check-order-status`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orderId, userId, planType })
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('checkOrderStatus error:', error);
    return { success: false, status: 'error', error: error.message };
  }
};

export default {
  loadRazorpayScript,
  createOrder,
  verifyPayment,
  openCheckout,
  checkOrderStatus
};
